<!-- props: [ "placeholder", "hintMessage", "status", "type", "label",
"passwordRevealToggle", "clearSearch", "forgotPassword", "validation", ],


pass in status to get desired input color,
type is a text for the inputType,
label is a text for the label of the input,
passwordRevealToggle is a boolean to show or hide the eye icon for toggling the input type between password and text, 
clearSearch is a boolean to show or hide the 'clear text' button which clears the input value,
forgotPassword is a boolean to show the forgot password button,
validation is a boolean to show or hide the password validation rules   .

to get desired input width, in the file where you are using the input wrap the button component in a div and apply a width to that wrapper div 
eg <div classname="w-[10%]"><EpumpInput/></div>

for inputs with icon pass in the icon as a slot eg: <EpumpInputVue><img src="@/assets/svg/lock.svg"/></EpumpInputVue>

example of what the input component might look like in your file:

<EpumpInputVue
:label="'Password'"
:passwordRevealToggle="true"
:clearSearch="false"
:placeholder="'Password'"
:status="'active'"
:type="'password'"
:forgotPassword="false"
:validation="false"
><img src="@/assets/svg/lock.svg"
/></EpumpInputVue> -->

<template>
  <div>
    <div v-if="label" class="mb-2">
      <label class="font-medium text-base leading-5 text-[#000000]">{{
        label
      }}</label>
    </div>

    <div
      :class="statusStyle.wrapperStyle"
      class="flex items-center justify-between w-full px-4 rounded-lg"
    >
      <div class="w-[90%] flex gap-4">
        <slot></slot>

        <input
          :required="required"
          :type="inputType"
          v-model="inputValue"
          :class="statusStyle.inputStyle"
          class="w-[100%] text-inputGrey-900 font-normal text-sm outline-none placeholder:font-normal placeholder:text-sm placeholder:leading-6"
          :placeholder="placeholder"
          autocomplete="on"
        />
      </div>
      <!-- <Eye /> -->

      <img
        v-if="passwordRevealToggle"
        @click="toggleView"
        :src="currentIcon"
        class="cursor-pointer"
      />

      <p
        v-if="clearSearch"
        @click="clearInput"
        class="text-sm font-medium text-red-400 cursor-pointer"
      >
        clear
      </p>
    </div>
    <div class="flex justify-between">
      <p class="text-[#D44333] font-normal text-sm leading-5 mt-1">
        {{ isEmail() ? emailValError : hintMessage }}
      </p>
      <p
        v-if="forgotPassword"
        class="text-[#595959] font-medium text-sm mt-1 flex justify-end cursor-pointer"
        @click="goToPage()"
      >
        Forgot password?
      </p>
    </div>

    <div v-if="validation" class="flex flex-col gap-2 mt-2">
      <div
        class="flex gap-3 align-items-center"
        v-for="(rule, index) in validationRuleList"
        :key="index"
      >
        <img :src="rule.image" alt="sun" />
        <p
          :class="{ 'text-green-500': rule.validated }"
          class="text-sm font-normal leading-5 text-inputGrey-900"
        >
          {{ rule.title }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import eye from "@/assets/svg/eye.svg";
import crossedEye from "@/assets/svg/crossedEye.svg";
import negativeEye from "@/assets/svg/negativeEye.svg";
import sun from "@/assets/svg/sun.svg";
import check from "@/assets/svg/check.svg";
import positiveEye from "@/assets/svg/positiveEye.svg";
export default {
  props: [
    "required",
    "placeholder",
    "hintMessage",
    "status",
    "type",
    "label",
    "passwordRevealToggle",
    "clearSearch",
    "forgotPassword",
    "validation",
  ],
  data() {
    return {
      emailValError: null,
      userIcons: {
        eye: eye,
        crossedEye: crossedEye,
        negativeEye: negativeEye,
        positiveEye: positiveEye,
      },
      validationImages: {
        sun: sun,
        check: check,
      },
      inputValue: "",
      inputType: this.type,
      currentIcon: "",
      statusStyle: {
        wrapperStyle: "py-3",
        inputStyle: "placeholder:text-inputGrey-900",
      },
      validationTimeout: null,

      validationRuleList: [
        {
          title: "Minimum contain 8 characters",
          validated: false,
          image: "",
        },
        {
          title: "Must contain uppercase",
          validated: false,
          image: "",
        },
        {
          title: "Must contain number",
          validated: false,
          image: "",
        },
        {
          title: "Must contain special character",
          validated: false,
          image: "",
        },
      ],
    };
  },
  mounted() {
    this.validationRuleList.forEach((test) => {
      test.image = this.validationImages.sun;
    });
    this.currentIcon = this.userIcons.eye;
    this.setInputStyle();
  },
  beforeUnmount() {
    // Clean up timeout to prevent memory leaks
    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout);
    }
  },
  watch: {
    status() {
      this.setInputStyle();
    },
    inputValue() {
      this.$emit("input", this.inputValue);

      // Clear previous timeout to debounce validation
      if (this.validationTimeout) {
        clearTimeout(this.validationTimeout);
      }

      // Debounce validation to prevent performance issues with paste
      this.validationTimeout = setTimeout(() => {
        if (this.isEmail()) {
          this.emailValidation(this.inputValue);
        } else {
          this.checkValidation();
        }
      }, 200);
    },
  },
  methods: {
    isEmail() {
      return this.type === "email";
    },
    emailValidation(email) {
      if (!email) return;
      const isValid = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,9})+$/.test(
        email,
      );

      if (!isValid) {
        this.emailValError = "invalid email";
      } else {
        this.emailValError = null;
      }
    },

    checkValidation() {
      const hasNumber = /\d/;
      const hasUppercase = /[A-Z]/;
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/;
      const hasMinLength = /.{8,}/; // At least 8 characters long

      if (hasMinLength.test(this.inputValue)) {
        this.validationRuleList[0].image = this.validationImages.check;
        this.validationRuleList[0].validated = true;
      } else {
        this.validationRuleList[0].image = this.validationImages.sun;
        this.validationRuleList[0].validated = false;
      }

      if (hasUppercase.test(this.inputValue)) {
        this.validationRuleList[1].image = this.validationImages.check;
        this.validationRuleList[1].validated = true;
      } else {
        this.validationRuleList[1].image = this.validationImages.sun;
        this.validationRuleList[1].validated = false;
      }

      if (hasNumber.test(this.inputValue)) {
        this.validationRuleList[2].image = this.validationImages.check;
        this.validationRuleList[2].validated = true;
      } else {
        this.validationRuleList[2].image = this.validationImages.sun;
        this.validationRuleList[2].validated = false;
      }

      if (hasSpecialChar.test(this.inputValue)) {
        this.validationRuleList[3].image = this.validationImages.check;
        this.validationRuleList[3].validated = true;
      } else {
        this.validationRuleList[3].image = this.validationImages.sun;
        this.validationRuleList[3].validated = false;
      }
    },
    clearInput() {
      this.inputValue = "";
      this.$emit("clear", this.inputValue);
    },
    setInputStyle() {
      switch (this.status) {
        case "positive":
          this.statusStyle.wrapperStyle =
            "border-[0.5px] border-estationsGreen-500 bg-inputGrey-500 py-3";
          this.statusStyle.inputStyle =
            "placeholder:text-estationsGreen-500 bg-inputGrey-500";
          this.currentIcon = this.userIcons.positiveEye;
          break;

        case "negative":
          this.statusStyle.wrapperStyle =
            "border-[0.5px] border-[#D44333] bg-inputGrey-500 py-3";
          this.statusStyle.inputStyle =
            "placeholder:text-[#D44333] bg-inputGrey-500";
          this.currentIcon = this.userIcons.negativeEye;
          break;

        case "disabled":
          this.statusStyle.wrapperStyle = "bg-inputGrey-300 py-3";
          this.statusStyle.inputStyle =
            "placeholder:text-inputGrey-600 bg-inputGrey-500";
          break;

        case "active":
          this.statusStyle.wrapperStyle = "bg-inputGrey-500 py-3";
          this.statusStyle.inputStyle =
            "placeholder:text-inputGrey-900 bg-inputGrey-500";
          this.currentIcon = this.userIcons.eye;
          break;

        case "dropdownSearch":
          this.statusStyle.wrapperStyle =
            "border border-[0.5px] border-inputGrey-500 bg-[#fff] py-3";
          this.statusStyle.inputStyle =
            "placeholder:text-inputGrey-900 bg-[#fff]";
          break;

        case "tableInput":
          this.statusStyle.wrapperStyle = "bg-inputGrey-500 py-[9px]";
          this.statusStyle.inputStyle =
            "placeholder:text-inputGrey-900 bg-inputGrey-500";
          this.currentIcon = this.userIcons.eye;
          break;
      }
    },

    updateValue() {},
    toggleView() {
      this.currentIcon =
        this.inputType == "password"
          ? this.userIcons.crossedEye
          : this.userIcons.eye;

      this.inputType == "password"
        ? (this.inputType = "text")
        : (this.inputType = "password");
    },
    goToPage() {
      this.$router.push("/forgot-password");
    },
  },
};
</script>

<style></style>
